import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, ButtonPro } from 'components/atoms';
import { AdminHeader, AdminSidebar } from 'components';
import { EmployeeReviewTable } from 'components/organisms/EmployeeReviewTable';
import { EmployeeReviewFilter } from 'components/organisms/EmployeeReviewFilter';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { getGoalAssignments } from 'store/Admin';
import { GoalAssignmentData } from 'models/models';
import { EmployeeReviewData } from 'components/pages/EmployeeReview';
import './index.scss';

export interface ScheduleReviewData {
  id: string;
  name: string; 
  fullName: string;
  userName: string;
  percentage: number;
  status: 'pending' | 'approved' | 'ongoing' | 'completed' | 'incomplete';
  acceptanceStatus: 'accepted' | 'declined' | 'pending' | 'mixed';
  team?: string;
  role?: string;
  avatar?: string;
  userRoleId: string;
  upn: string;
  epfNumber: string;
  designation?: string | null;
  employeeNickName: string;
  isActive: boolean;
  performance?: {
    totalGoals: number;
    completedGoals: number;
    pendingGoals: number;
    inProgressGoals: number;
    acceptedGoals: number;
    declinedGoals: number;
    totalSubtasks: number;
    completedSubtasks: number;
    pendingSubtasks: number;
    inProgressSubtasks: number;
    goalsCompletionRate: number;
    subtasksCompletionRate: number;
    overallPerformanceRate: number;
  };
  goalDetails?: {
    goalTitle: string;
    acceptanceStatus: 'Accepted' | 'Declined' | 'Pending';
  }[];
}

export const ScheduleReview = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [acceptanceStatusFilter, setAcceptanceStatusFilter] = useState('all');
  const [goalStatusFilter, setGoalStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const goalAssignments = useAppSelector((state: RootState) => state.admin.goalAssignments);

  const transformGoalAssignmentData = (assignments: GoalAssignmentData[]): ScheduleReviewData[] => {
    return assignments.map((assignment) => {
      const employee = assignment.employee[0]; 
      
      let acceptanceStatus: 'accepted' | 'declined' | 'pending' | 'mixed' = 'pending';
      let goalDetails: { goalTitle: string; acceptanceStatus: 'Accepted' | 'Declined' | 'Pending' }[] = [];
      
      if (assignment.performance) {
        const { acceptedGoals = 0, declinedGoals = 0, totalGoals = 0 } = assignment.performance;
        
        const acceptedCount = acceptedGoals;
        const declinedCount = declinedGoals;
        const pendingCount = totalGoals - acceptedCount - declinedCount;
        
        for (let i = 1; i <= acceptedCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${i}`,
            acceptanceStatus: 'Accepted'
          });
        }
        for (let i = 1; i <= declinedCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${acceptedCount + i}`,
            acceptanceStatus: 'Declined'
          });
        }
        for (let i = 1; i <= pendingCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${acceptedCount + declinedCount + i}`,
            acceptanceStatus: 'Pending'
          });
        }
        
        if (totalGoals > 0) {
          if (acceptedCount > 0 && declinedCount === 0) {
            acceptanceStatus = 'accepted';
          } else if (acceptedCount > 0 && declinedCount > 0) {
            acceptanceStatus = 'declined';
          } else if (acceptedCount === 0 && declinedCount === 0) {
            acceptanceStatus = 'pending';
          } else if (acceptedCount === 0 && declinedCount > 0) {
            acceptanceStatus = 'declined';
          } else {
            acceptanceStatus = 'pending';
          }
        }
      }
      
      return {
        id: employee?.id || '',
        name: employee?.fullName || employee?.userName || 'Unknown Employee',
        fullName: employee?.fullName || '',
        userName: employee?.userName || '',
        percentage: Math.round(assignment.performance.overallPerformanceRate),
        status: (() => {
          const { completedGoals = 0, totalGoals = 0, inProgressSubtasks = 0 } = assignment.performance;
          
          if (completedGoals === totalGoals && totalGoals > 0) {
            return 'completed';
          } else if (inProgressSubtasks > 0) {
            return 'ongoing';
          } else {
            return 'pending';
          }
        })(),
        acceptanceStatus,
        team: '',
        role: employee?.designation || 'No Designation',
        avatar: employee?.fullName ? employee.fullName.split(' ').map(n => n[0]).join('').toUpperCase() :
          employee?.userName ? employee.userName.substring(0, 2).toUpperCase() : 'NA',
        userRoleId: employee?.id || '',
        upn: employee?.email || '',
        epfNumber: employee?.epfNumber || '',
        designation: employee?.designation || null,
        employeeNickName: employee?.userName || employee?.fullName || '',
        isActive: true,
        performance: assignment.performance,
        goalDetails
      };
    });
  };

  const employeeReviews = useMemo(() => transformGoalAssignmentData(goalAssignments), [goalAssignments]);

  const convertToEmployeeReviewData = (scheduleData: ScheduleReviewData[]): EmployeeReviewData[] => {
    return scheduleData.map(employee => ({
      id: employee.epfNumber,
      name: employee.userName,
      percentage: employee.percentage,
      status: employee.status,
      role: employee.role,
      avatar: employee.avatar,
      userRoleId: employee.userRoleId,
      upn: employee.upn,
      epfNumber: employee.epfNumber,
      designation: employee.designation,
      employeeNickName: employee.employeeNickName,
      isActive: employee.isActive,
      acceptanceStatus: employee.acceptanceStatus,
      goalDetails: employee.goalDetails,
      performance: employee.performance
    }));
  };

  const filteredEmployees = useMemo(() => {
    let filtered = convertToEmployeeReviewData(employeeReviews);

    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(
        (employee) =>
          employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.upn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.epfNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employeeNickName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply acceptance status filter
    if (acceptanceStatusFilter !== 'all') {
      filtered = filtered.filter(employee => employee.acceptanceStatus === acceptanceStatusFilter);
    }

    // Apply goal status filter
    if (goalStatusFilter !== 'all') {
      filtered = filtered.filter(employee => employee.status === goalStatusFilter);
    }

    return filtered;
  }, [employeeReviews, searchTerm, acceptanceStatusFilter, goalStatusFilter]);

  const handleEmployeeClick = (employee: EmployeeReviewData) => {
    navigate(`/admin/schedule-review/${employee.epfNumber}/tasks`, { state: { employee } });
  };

  const handleBackToDashboard = () => {
    navigate('/admin/employee-review');
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); 
  };

  const handleAcceptanceStatusFilter = (status: string) => {
    setAcceptanceStatusFilter(status);
    setCurrentPage(1);
  };

  const handleGoalStatusFilter = (status: string) => {
    setGoalStatusFilter(status);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); 
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  useEffect(() => {
    if (goalAssignments.length === 0) {
      dispatch(getGoalAssignments());
    }
  }, [dispatch, goalAssignments.length]);

  return (
    <>
      <AdminHeader />
      <div className="dashboard-wrapper">
        <AdminSidebar />
        <div className="schedule-review-main-wrapper">
          <ButtonPro
            label="Back to Dashboard"
            onClick={handleBackToDashboard}
            type="white"
            icon="ri-arrow-left-line"
          />
          <div className="schedule-review-header">
            <div className="header-content">
              <Typography type="heading4" text="Schedule Employee Reviews" weight="bold" />
              <Typography type="small_regular" text="Select an employee to schedule or view their review tasks" color="gray" />
            </div>
            <EmployeeReviewFilter 
              onSearch={handleSearch} 
              searchTerm={searchTerm}
              onAcceptanceStatusFilter={handleAcceptanceStatusFilter}
              acceptanceStatusFilter={acceptanceStatusFilter}
              onGoalStatusFilter={handleGoalStatusFilter}
              goalStatusFilter={goalStatusFilter}
            />
          </div>

          <div className="schedule-review-content">
            <EmployeeReviewTable
              data={paginatedEmployees}
              onEmployeeClick={handleEmployeeClick}
              showScheduleColumn={true}
              showAcceptanceStatusColumn={true}
              showNextReviewColumn={false}
              showPagination={true}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredEmployees.length}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
}; 